import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { BasicInput } from '@dev/ee-components';
import { useIsTypeAB } from '@/components/CampaignCreator/hooks/useIsTypeAB';
import { ErrorSubscriberOrder } from '@/components/CampaignCreator/CampaignCreatorInterface';
import { useGetSettingsInfo } from '@/components/CampaignCreator/SettingsAndTracking/hooks/useGetStep3Info';
import {
    ModifyCampaignMap,
    SetCampaignErrorsSubscriber,
    getCampainCreatorStore,
    VerifyCampaign,
} from '@/store/campaignCreator/actions';
import { useCampaignScreensPathsChecker } from '@/components/CampaignCreator/hooks/useCampaignScreensPathsChecker';
import { UTMInputWrapper } from './styles';
import { UTMInputsProps } from './UTMInputsInterface';

const UTMInputs: FC<UTMInputsProps> = ({
    index,
    sourceErrorName,
    mediumErrorName,
    campaginErrorName,
    contentErrorName,
}): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isTypeAB = useIsTypeAB();
    const { errors } = useSelector(getCampainCreatorStore);
    const { isDetailsScreen } = useCampaignScreensPathsChecker();
    const { utmSourceValue, utmMediumValue, utmCampaignValue, utmContentValue } = useGetSettingsInfo(index);

    const sourceId = `campaign-creator-utm-source-input-${index}`;
    const mediumId = `campaign-creator-utm-medium-input-${index}`;
    const camapginId = `campaign-creator-utm-campaign-input-${index}`;
    const contentId = `campaign-creator-utm-content-input-${index}`;

    const isUTMSourceError = !!errors?.[sourceErrorName];
    const isUTMMediumError = !!errors?.[mediumErrorName];
    const isUTMCampaignError = !!errors?.[campaginErrorName];
    const isUTMContentError = !!errors?.[contentErrorName];

    useEffect((): void => {
        dispatch(
            SetCampaignErrorsSubscriber({
                id: sourceId,
                errorName: sourceErrorName,
                order: ErrorSubscriberOrder.UTM,
            })
        );
        dispatch(
            SetCampaignErrorsSubscriber({
                id: mediumId,
                errorName: mediumErrorName,
                order: ErrorSubscriberOrder.UTM,
            })
        );
        dispatch(
            SetCampaignErrorsSubscriber({
                id: camapginId,
                errorName: campaginErrorName,
                order: ErrorSubscriberOrder.UTM,
            })
        );
        if (isTypeAB) {
            dispatch(
                SetCampaignErrorsSubscriber({
                    id: contentId,
                    errorName: contentErrorName,
                    order: ErrorSubscriberOrder.UTM,
                })
            );
        }
    }, [index]);

    const changeUTMSource = (ev): void => {
        const modifyMap = new Map<string, any>();
        modifyMap.set(`CampaignTemplates[${index}].UTM.UtmSource`, ev?.target?.value);
        dispatch(ModifyCampaignMap(modifyMap));
        isUTMSourceError && dispatch(VerifyCampaign());
    };

    const changeUTMMedium = (ev): void => {
        const modifyMap = new Map<string, any>();
        modifyMap.set(`CampaignTemplates[${index}].UTM.UtmMedium`, ev?.target?.value);
        dispatch(ModifyCampaignMap(modifyMap));
        isUTMMediumError && dispatch(VerifyCampaign());
    };

    const changeUTMCampaign = (ev): void => {
        const modifyMap = new Map<string, any>();
        modifyMap.set(`CampaignTemplates[${index}].UTM.UtmCampaign`, ev?.target?.value);
        dispatch(ModifyCampaignMap(modifyMap));
        isUTMCampaignError && dispatch(VerifyCampaign());
    };

    const changeUTMContent = (ev): void => {
        const modifyMap = new Map<string, any>();
        modifyMap.set(`CampaignTemplates[${index}].UTM.UtmContent`, ev?.target?.value);
        dispatch(ModifyCampaignMap(modifyMap));
        isUTMContentError && dispatch(VerifyCampaign());
    };

    return (
        <>
            <UTMInputWrapper>
                <BasicInput
                    id={sourceId}
                    disableSentenceCase
                    label={t('utmsource')}
                    onChange={changeUTMSource}
                    value={utmSourceValue}
                    error={isUTMSourceError}
                    errorMessage={t('utm_containsspace_error')}
                    readOnly={isDetailsScreen}
                />
            </UTMInputWrapper>
            <UTMInputWrapper>
                <BasicInput
                    id={mediumId}
                    disableSentenceCase
                    label={t('utmmedium')}
                    onChange={changeUTMMedium}
                    value={utmMediumValue}
                    error={isUTMMediumError}
                    errorMessage={t('utm_containsspace_error')}
                    readOnly={isDetailsScreen}
                />
            </UTMInputWrapper>
            <UTMInputWrapper>
                <BasicInput
                    id={camapginId}
                    disableSentenceCase
                    label={t('utmcampaign')}
                    onChange={changeUTMCampaign}
                    value={utmCampaignValue}
                    error={isUTMCampaignError}
                    errorMessage={t('utm_containsspace_error')}
                    readOnly={isDetailsScreen}
                />
            </UTMInputWrapper>
            {isTypeAB && (
                <UTMInputWrapper>
                    <BasicInput
                        id={camapginId}
                        disableSentenceCase
                        label={t('utmcontent')}
                        onChange={changeUTMContent}
                        value={utmContentValue}
                        error={isUTMContentError}
                        errorMessage={t('utm_containsspace_error')}
                        readOnly={isDetailsScreen}
                    />
                </UTMInputWrapper>
            )}
        </>
    );
};

export default UTMInputs;
